import { Injectable } from '@angular/core';
import { StorageService } from '@app/core/services/storage/storage.service';
import { StorageRoles } from '@app/core/constants';
import { AuthRoles } from '@app/modules/auth-modules/auth.model';
import {
  AccountProfileVm,
  AccountUserInfo,
  AccountUserTypes,
  hasBookingCustomerAccount,
  hasMusicianAccountProfile
} from '@app/modules/account-modules/account.model';

@Injectable()

export class RolesService {
  constructor(
    private storage: StorageService
  ) {}

  /**
   * Setter for current roles.
   * @param value
   * @param remember
   */
  onRoles(value: Array<AuthRoles>, remember: boolean = true) {
    this.storage.setStorage({
      key: StorageRoles.roles,
      value: value
    }, remember ? 'local' : 'session');
  }

  /**
   * Persist GET /account/userInfo `profiles` for guards and UI that only read storage.
   */
  onAccountProfiles(value: AccountProfileVm[] | null | undefined, remember: boolean = true): void {
    const payload = value && value.length ? value : [];
    this.storage.setStorage({
      key: StorageRoles.profiles,
      value: payload
    }, remember ? 'local' : 'session');
  }

  /**
   * Getter for current roles.
   */
  get roles(): Array<AuthRoles> {
    const rolesStorage  = (
      this.storage.getStorage({ key: StorageRoles.roles }, 'local') ||
      this.storage.getStorage({ key: StorageRoles.roles }, 'session') ||
      '[]'
    );
    return JSON.parse(rolesStorage);
  }

  get accountProfiles(): AccountProfileVm[] {
    const raw =
      this.storage.getStorage({ key: StorageRoles.profiles }, 'local') ||
      this.storage.getStorage({ key: StorageRoles.profiles }, 'session') ||
      '[]';
    try {
      return JSON.parse(raw) as AccountProfileVm[];
    } catch {
      return [];
    }
  }

  /**
   * Musician extension: active Musician row in GET /account/userInfo `profiles`.
   * @param user
   */
  onUserRole(user: AccountUserInfo): boolean {
    return !!user && hasMusicianAccountProfile(user);
  }


  /**
   * Checking of UserRole on Admin
   * @param user
   */
  onAdminRole(user: AccountUserInfo): boolean {
    return user && user.roles.some((role: AccountUserTypes) => role === 'admin');
  }

  /**
   * Checking of UserRole on Moderator
   * @param user
   */
  onModeratorRole(user: AccountUserInfo): boolean {
    return user && user.roles.some((role: AccountUserTypes) => role === 'moderator');
  }

  /**
   * Checking of UserRole on Partner
   * @param user
   */
  onPartnerRole(user: AccountUserInfo): boolean {
    return user && user.roles.some((role: AccountUserTypes) => role === 'partner');
  }

  onBookingCustomerOnlyRole(user: AccountUserInfo): boolean {
    if (!user) {
      return false;
    }
    const hasCustomer = hasBookingCustomerAccount(user);
    const hasMusician = hasMusicianAccountProfile(user);
    return hasCustomer && !hasMusician;
  }
}
